import { setStatus } from './status'

export interface StepNavOptions {
  form: HTMLFormElement
  /** Return true to allow leaving the given step (runs on Next, not Back). */
  onValidateStep: (stepIndex: number) => boolean
  /** Called after a step becomes visible (e.g. render the review summary). */
  onStepChange?: (stepIndex: number) => void
}

export interface StepNav {
  current(): number
  stepCount: number
  goTo(index: number): void
  /** Jump to a step from a review-page Edit link; the next successful Next
      returns straight to the review step instead of the sequential one. */
  goToForEdit(index: number): void
}

const NEXT_LABELS = [
  'Next: Vehicle Information',
  'Next: Location & Pickup',
  'Next: Review & Submit',
]

export function initStepNavigation(opts: StepNavOptions): StepNav {
  const { form } = opts
  const steps = Array.from(form.querySelectorAll<HTMLElement>('.form-step'))
  const pills = Array.from(form.querySelectorAll<HTMLElement>('.step-pill'))
  const stepsBar = form.querySelector<HTMLElement>('.steps')
  const backBtn = document.getElementById('backBtn') as HTMLButtonElement
  const nextBtn = document.getElementById('nextBtn') as HTMLButtonElement
  const submitBtn = document.getElementById('submitBtn') as HTMLButtonElement

  let currentStep = 0
  // Set while the user is editing a step via a review-page Edit link: Next
  // returns straight to this step; Back cancels and resumes sequential flow.
  let returnStep: number | null = null

  function showStep(index: number, focusHeading: boolean): void {
    currentStep = index
    const last = steps.length - 1

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    steps.forEach((step, i) => step.classList.toggle('active', i === index))
    pills.forEach((pill, i) => {
      pill.classList.toggle('active', i === index)
      if (i === index) pill.setAttribute('aria-current', 'step')
      else pill.removeAttribute('aria-current')
    })

    // Center the active pill when the pills overflow horizontally (mobile).
    // Scrolling the container directly (not scrollIntoView) so the page and
    // the embedding parent frame never scroll as a side effect.
    const activePill = pills[index]
    if (stepsBar && activePill && stepsBar.scrollWidth > stepsBar.clientWidth) {
      const delta = activePill.getBoundingClientRect().left - stepsBar.getBoundingClientRect().left
      stepsBar.scrollTo({
        left: stepsBar.scrollLeft + delta - (stepsBar.clientWidth - activePill.offsetWidth) / 2,
        behavior: reducedMotion ? 'auto' : 'smooth',
      })
    }

    backBtn.style.display = index === 0 ? 'none' : 'inline-block'
    nextBtn.style.display = index === last ? 'none' : 'inline-block'
    submitBtn.style.display = index === last ? 'inline-block' : 'none'
    nextBtn.textContent =
      (returnStep !== null ? NEXT_LABELS[NEXT_LABELS.length - 1] : NEXT_LABELS[index]) ?? 'Next'

    setStatus(`Step ${index + 1} of ${steps.length}`)
    opts.onStepChange?.(index)

    window.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' })

    if (focusHeading) {
      // Land screen-reader/keyboard users on the new step's heading.
      const heading = steps[index]?.querySelector<HTMLElement>('.section-title')
      heading?.focus({ preventScroll: true })
    }
  }

  nextBtn.addEventListener('click', () => {
    if (opts.onValidateStep(currentStep)) {
      const target = returnStep ?? Math.min(currentStep + 1, steps.length - 1)
      returnStep = null
      showStep(target, true)
    }
  })

  backBtn.addEventListener('click', () => {
    returnStep = null
    showStep(Math.max(currentStep - 1, 0), true)
  })

  // Enter inside a text input/select advances the current step instead of
  // submitting the whole form mid-way. The last step keeps native submit.
  form.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key !== 'Enter') return
    const target = e.target as HTMLElement
    const tag = target.tagName
    if (tag !== 'INPUT' && tag !== 'SELECT') return
    if ((target as HTMLInputElement).type === 'checkbox') return
    if (currentStep < steps.length - 1) {
      e.preventDefault()
      nextBtn.click()
    }
  })

  // Initial render: no focus steal on page load.
  showStep(0, false)

  return {
    current: () => currentStep,
    stepCount: steps.length,
    goTo: (index: number) => showStep(index, true),
    goToForEdit: (index: number) => {
      returnStep = steps.length - 1
      showStep(index, true)
    },
  }
}
